import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../lib/utils';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 disabled:pointer-events-none disabled:opacity-50 shadow-theme-xs [&_svg]:pointer-events-none [&_svg:not([class*=size-])]:size-4 has-[>svg]:px-3',
  {
    variants: {
      variant: {
        default: 'bg-brand-500 text-white hover:bg-brand-600 shadow-lg shadow-brand-500/25',
        secondary: 'bg-gray-900 text-white hover:bg-gray-800',
        primary: 'bg-brand-500 text-white hover:bg-brand-600 shadow-lg shadow-brand-500/25',
        destructive: 'bg-error-500 text-white hover:bg-error-600',
        outline:
          'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 ring-1 ring-inset ring-gray-300 hover:ring-gray-400',
        ghost: 'shadow-none text-gray-700 hover:bg-gray-100',
        link: 'shadow-none text-brand-500 underline-offset-4 hover:underline',
        warning: 'bg-warning-500 text-white hover:bg-warning-600',
        success: 'bg-success-500 text-white hover:bg-success-600',
      },
      size: {
        default: 'h-11 px-5 py-3 text-sm',
        sm: 'h-9 px-4 py-3 text-sm',
        lg: 'h-12 px-6 text-xs',
        icon: 'h-11 w-11',
        'icon-sm': 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
  ),
);
Button.displayName = 'Button';
